import axios, { AxiosResponse } from 'axios';

// Create axios instance for leads API calls
const leadsApi = axios.create({
  baseURL: '/api', // Use relative URL to go through Vite proxy
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
leadsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('redex_admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
leadsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('redex_admin_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types for Lead data
export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  source: 'website-cta' | 'labs-booking' | 'referral' | 'manual' | 'other';
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'unqualified' | 'lost';
  service_interest: string | null;
  mrr_range: string | null;
  tech_expenses: string | null;
  software_interaction: string | null;
  message: string | null;
  notes: string | null;
  assigned_to: string | null;
  contacted_at: string | null;
  qualified_at: string | null;
  converted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadActivity {
  id: string;
  contact_id: string;
  user_id: string | null;
  activity_type: 'email' | 'call' | 'meeting' | 'note' | 'status_change';
  subject: string | null;
  description: string | null;
  activity_date: string;
  created_at: string;
}

export interface CreateLeadRequest {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  source?: Lead['source'];
  status?: Lead['status'];
  service_interest?: string;
  mrr_range?: string;
  tech_expenses?: string;
  software_interaction?: string;
  message?: string;
  notes?: string;
}

export interface UpdateLeadRequest {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  status?: Lead['status'];
  service_interest?: string;
  notes?: string;
  assigned_to?: string;
}

export interface LeadsResponse {
  leads: Lead[];
  total: number;
}

export interface LeadFilters {
  status?: string;
  source?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

class LeadsService {
  /**
   * Get all leads with filters
   */
  async getLeads(filters?: LeadFilters): Promise<LeadsResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.source) params.append('source', filters.source);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response: AxiosResponse<{ success: boolean; data: LeadsResponse }> = 
      await leadsApi.get(`/leads?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get a single lead by ID
   */
  async getLead(id: string): Promise<Lead> {
    const response: AxiosResponse<{ success: boolean; data: Lead }> = 
      await leadsApi.get(`/leads/${id}`);
    return response.data.data;
  }

  /**
   * Create a new lead
   */
  async createLead(lead: CreateLeadRequest): Promise<Lead> {
    const response: AxiosResponse<{ success: boolean; data: Lead }> = 
      await leadsApi.post('/leads', lead);
    return response.data.data;
  }

  /**
   * Update a lead
   */
  async updateLead(id: string, data: UpdateLeadRequest): Promise<Lead> {
    const response: AxiosResponse<{ success: boolean; data: Lead }> = 
      await leadsApi.put(`/leads/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete a lead
   */
  async deleteLead(id: string): Promise<void> {
    await leadsApi.delete(`/leads/${id}`);
  }

  /**
   * Update lead status
   */
  async updateLeadStatus(id: string, status: Lead['status']): Promise<Lead> {
    const response: AxiosResponse<{ success: boolean; data: Lead }> = 
      await leadsApi.put(`/leads/${id}/status`, { status });
    return response.data.data;
  }

  /**
   * Get lead activities
   */
  async getLeadActivities(leadId: string): Promise<LeadActivity[]> {
    const response: AxiosResponse<{ success: boolean; data: LeadActivity[] }> = 
      await leadsApi.get(`/leads/${leadId}/activities`);
    return response.data.data;
  }

  /**
   * Add activity to a lead
   */
  async addLeadActivity(leadId: string, activity: {
    activity_type: LeadActivity['activity_type'];
    subject?: string;
    description?: string;
  }): Promise<LeadActivity> {
    const response: AxiosResponse<{ success: boolean; data: LeadActivity }> = 
      await leadsApi.post(`/leads/${leadId}/activities`, activity);
    return response.data.data;
  }

  /**
   * Convert lead to client
   */
  async convertToClient(leadId: string): Promise<{ clientId: string; message: string }> {
    const response: AxiosResponse<{ success: boolean; data: { clientId: string; message: string } }> = 
      await leadsApi.post(`/leads/${leadId}/convert`);
    return response.data.data;
  }
}

export const leadsService = new LeadsService();
